import React, { useEffect, useState, useRef } from 'react'
import { useAppData } from '../../context/appContext';
// import DTAL from '../../service/devicesubscription/mockdataservice';
import { IonCol, IonGrid, IonRow, IonLabel, IonSpinner, IonButton, IonIcon } from '@ionic/react';
import { downloadOutline } from 'ionicons/icons';
import moment from 'moment';
import 'react-circular-progressbar/dist/styles.css';
import "./patientlog.css"
import { useContext } from 'react';
import AppContext from '../../components/AppContext';
import ErrorLoggingService from '../../service/ErrorLoggingService';
import { formatAppDateTime } from '../../utils/dateFormat';

const BATCH_INTERVAL = 3000; // process and display symptoms every 3s during SSE
const SSE_INACTIVITY_PERIOD = 5000; // 5s no new events = done
const SIX_MONTHS_MS = 15552000000; // 180 days
const STREAM_KEY = 'symptomHistory'; // dedicated key to avoid conflicts with other pages
let patientSymptoms = new Map();
let loadedForPatient = ""; // track which patient the cached data belongs to
let selectedPatient = "" //patient ID
const Patientlog = ({selectedPatientSubject}) => {
    const appData = useAppData()
    const [symptomsLog, setSymptomsLog] = useState()
    const [isLoaded,setIsLoaded] = useState(false)
    const [isSymptomAvailable,setIsSymptomAvailable] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const {dtalService} = useContext(AppContext)
    const bottomRef = useRef(null)
    
    const patients = appData.allPatientData || []
    

    useEffect(() => {
        let batchTimer = null;
        let inactivityTimer = null;
        let sseSubscription = null;
        let isDone = false;

        const fetchSymptoms = async (patientId) => {
            // Reuse cached data if already loaded for this patient (handles Ionic remount)
            if (patientId === loadedForPatient && patientSymptoms.size > 0) {
                setSymptomsLog(Array.from(patientSymptoms.keys()));
                setIsLoaded(true);
                setLoadingMore(false);
                setIsSymptomAvailable(true);
                return;
            }

            // Clean up any previous SSE
            isDone = false;
            if (batchTimer) { clearInterval(batchTimer); batchTimer = null; }
            if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; }
            if (sseSubscription) { sseSubscription.unsubscribe(); sseSubscription = null; }
            dtalService.unsubscribe(STREAM_KEY);

            setIsLoaded(false);
            setLoadingMore(false);
            setSymptomsLog([]);
            patientSymptoms.clear();
            loadedForPatient = patientId;

            const now = new Date();
            const sixMonthsAgo = new Date(now.getTime() - SIX_MONTHS_MS);

            let sseSubject;
            try {
                sseSubject = await dtalService.gethistorical(
                    "symptom:" + patientId, null, null,
                    sixMonthsAgo.toISOString(), now.toISOString(),
                    "thingId,attributes,features,_revision,_modified",
                    STREAM_KEY
                );
            } catch (err) {
                ErrorLoggingService.error("Error starting symptom SSE", { patientId, err });
                setIsLoaded(true);
                setIsSymptomAvailable(false);
                return;
            }

            if (!sseSubject) {
                setIsLoaded(true);
                setIsSymptomAvailable(false);
                return;
            }

            // SSE completion handler
            const finish = () => {
                if (isDone) return;
                isDone = true;
                if (batchTimer) { clearInterval(batchTimer); batchTimer = null; }
                if (inactivityTimer) { clearTimeout(inactivityTimer); inactivityTimer = null; }
                dtalService.unsubscribe(STREAM_KEY);
                if (sseSubscription) { sseSubscription.unsubscribe(); sseSubscription = null; }

                // Final render
                setSymptomsLog(Array.from(patientSymptoms.keys()));
                setIsLoaded(true);
                setLoadingMore(false);
                if (patientSymptoms.size === 0) {
                    setIsSymptomAvailable(false);
                } else {
                    setIsSymptomAvailable(true);
                }
            };

            // Batch timer: progressively display symptoms every 3s during SSE
            setLoadingMore(true);
            batchTimer = setInterval(() => {
                if (patientSymptoms.size > 0) {
                    setSymptomsLog(Array.from(patientSymptoms.keys()));
                    setIsLoaded(true);
                    setIsSymptomAvailable(true);
                }
            }, BATCH_INTERVAL);

            // Subscribe to SSE events
            sseSubscription = sseSubject.subscribe({
                next: (response) => {
                    if (isDone) return;
                    if (inactivityTimer) clearTimeout(inactivityTimer);

                    if (patientSymptoms.has(response._modified)) {
                        finish(); // duplicate = all historical data received
                        return;
                    }

                    patientSymptoms.set(response._modified, response);
                    inactivityTimer = setTimeout(finish, SSE_INACTIVITY_PERIOD);
                },
                // Stream completed (server closed connection) — finalize immediately
                complete: () => {
                    finish();
                }
            });

            // Fallback: if no events arrive at all, finish after inactivity period
            inactivityTimer = setTimeout(finish, SSE_INACTIVITY_PERIOD);
        };

        if (appData.selected !== "Patient Log") appData.setSelected("Patient Log")
        const selectedPatientSubscription = selectedPatientSubject.subscribe(selectedPatientId => {
            if(!selectedPatientId) {
                if (appData?.appUserData == null) {
                    selectedPatient = patients && patients.length > 0 ? patients[0]?.attributes.subjectId : ""
                } else {
                    selectedPatient = appData?.appUserData.attributes.subjectId
                }
            } else {
                selectedPatient = selectedPatientId;
            }

            if(selectedPatient.length) {
                fetchSymptoms(selectedPatient);
            }
        })

    return () => {
      isDone = true;
      if (batchTimer) clearInterval(batchTimer);
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (sseSubscription) sseSubscription.unsubscribe();
      dtalService.unsubscribe(STREAM_KEY);
      selectedPatientSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Auto-scroll to the last symptom when list updates
    useEffect(() => {
        if (symptomsLog && symptomsLog.length > 0 && !loadingMore) {
            setTimeout(() => {
                bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
    }, [symptomsLog, loadingMore])

    // SRS-WEB-047: Symptom log export for clinical documentation
    const exportSymptomLog = () => {
        if (!symptomsLog || symptomsLog.length === 0) {
            ErrorLoggingService.warn("No symptoms to export", { selectedPatient });
            return;
        }

        const groupedBySymptom = new Map();
        symptomsLog.forEach((key) => {
            const info = patientSymptoms.get(key);
            const props = info?.features?.content?.properties || {};
            const symptomName = props?.name || "";
            const message = props?.message ?? "";
            const time = props?.timeOfOccurence;
            if (!symptomName || (!message && !time)) return;
            if (!groupedBySymptom.has(symptomName)) groupedBySymptom.set(symptomName, []);
            groupedBySymptom.get(symptomName).push({ message, time });
        });

        // Create CSV content
        let csvContent = "Symptom,Time Reported,Message\n";
        Array.from(groupedBySymptom.entries()).forEach(([symptomName, entries]) => {
            entries.forEach((e) => {
                const timeFormatted = e.time ? formatAppDateTime(moment(e.time).local(), { separator: ', ' }) : '';
                const messageEscaped = (e.message || '').replace(/"/g, '""');
                csvContent += `"${symptomName}","${timeFormatted}","${messageEscaped}"\n`;
            });
        });

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `symptom_log_${selectedPatient}_${moment().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        ErrorLoggingService.info("Symptom log exported", { 
            selectedPatient, 
            symptomCount: symptomsLog.length 
        });
    };

    return (
        <div data-testid="TPATIENTLOG" className='patientlog_container'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '0 20px' }}>
                <h3 style={{ margin: 0 }}>Patient Symptom Log</h3>
                {isLoaded && symptomsLog && symptomsLog.length > 0 && (
                    <IonButton 
                        onClick={exportSymptomLog} 
                        fill="outline" 
                        size="default"
                        style={{ 
                            '--color': '#ffffff',
                            color: '#ffffff',
                            fontWeight: 'normal'
                        }}
                    >
                        <IonIcon icon={downloadOutline} slot="start" style={{ color: '#ffffff' }} />
                        <span style={{ color: '#ffffff', marginLeft: '8px' }}>Export CSV</span>
                    </IonButton>
                )}
            </div>
            {!isLoaded && (
                <div style={{display: 'flex', alignItems: 'center', padding: '20px'}}>
                    <IonSpinner name="crescent" />
                    <IonLabel style={{marginLeft: '10px'}}>Loading Symptoms log, please wait...</IonLabel>
                </div>
            )}
            {isLoaded && !isSymptomAvailable && (
                <div style={{padding: '20px', textAlign: 'center'}}>
                    <IonLabel style={{fontSize: '15px', color: '#999'}}>No Symptoms to show for this Patient</IonLabel>
                </div>
            )}
            {(() => {
                if (!symptomsLog || symptomsLog.length === 0) return null;

                const groupedBySymptom = new Map();
                symptomsLog.forEach((key) => {
                    const info = patientSymptoms.get(key);
                    const props = info?.features?.content?.properties || {};
                    const symptomName = props?.name || "";
                    const message = props?.message ?? "";
                    const time = props?.timeOfOccurence;
                    // skip entries with no meaningful values or missing name
                    if (!symptomName || (!message && !time)) return;
                    if (!groupedBySymptom.has(symptomName)) groupedBySymptom.set(symptomName, []);
                    groupedBySymptom.get(symptomName).push({ message, time });
                });

                const groupedArray = Array.from(groupedBySymptom.entries()).filter(([name, entries]) => name && entries.length > 0);

                return groupedArray.map(([symptomName, entries], rowIndex) => {
                    return (
                        <div key={symptomName + '_' + rowIndex} className={`plog_row ${rowIndex % 2 === 0 ? 'plog_row--alt' : ''}`}>
                            <IonGrid>
                                <IonRow style={{ alignItems: 'flex-start' }}>
                                    <IonCol size='3'>
                                        <div>
                                            <div className='plog_label'>Symptom</div>
                                            <div data-testid={'TPATIENTLOGNAME_' + rowIndex} className='plog_symptom_name'>{symptomName}</div>
                                            {(() => {
                                                const latestTimeMs = entries.reduce((acc, e) => (e?.time && e.time > acc ? e.time : acc), 0);
                                                const latestFormatted = latestTimeMs ? formatAppDateTime(moment(latestTimeMs).local(), { separator: ', ' }) : '';
                                                return (
                                                    <div style={{ marginTop: 6 }}>
                                                        <div className='plog_label'>Time Reported</div>
                                                        <div className='plog_time'>{latestFormatted}</div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </IonCol>
                                    <IonCol size='9'>
                                        {entries.map((e, i) => {
                                            return (
                                                <div key={i} className='plog_pair'>
                                                    <div className='plog_value' data-testid={'TPATIENTLOGNOTE_' + rowIndex + '_' + i}>
                                                        <span className='plog_label'>Message: </span>
                                                        <span>{e?.message || ''}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </IonCol>
                                </IonRow>
                            </IonGrid>
                        </div>
                    );
                });
            })()}
            {loadingMore && isLoaded && (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '15px', color: '#666'}}>
                    <IonSpinner name="crescent" style={{width: '20px', height: '20px', marginRight: '8px'}} />
                    <span style={{fontSize: '13px'}}>Loading more symptoms ({patientSymptoms.size} records)...</span>
                </div>
            )}
            <div ref={bottomRef} />
        </div>
    )
}

export default Patientlog